import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { useGuestLoginMutation } from '@/api/queries/auth';
import { AppRoute } from '@/common/app/routes';
import { getDeviceId } from '@/common/lib/device-id';
import { Loader } from '@/components/ui/Loader/Loader';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';

/**
 * "Play as guest" — native app only. The same device always gets the same
 * account back; the player can attach Telegram later from the settings.
 */
type Props = {
  className?: string;
  label?: string;
};

const GuestLoginButton: FC<Props> = ({ className, label }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [guestLogin] = useGuestLoginMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const deviceId = await getDeviceId();
      // Token goes to the store via userReducer (guestLogin.matchFulfilled)
      const user = await guestLogin({ deviceId }).unwrap();

      if (user.token) localStorage.setItem('auth_token', user.token);
      navigate(AppRoute.MAIN);
    } catch {
      showToast(t('Something went wrong, please try again'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleLogin}
      disabled={isLoading}
      className={cn(
        className ??
          'w-full h-12 flex items-center justify-center gap-2 rounded-lg bg-black/5 border border-black/10 text-[#1a1a1a] active:scale-95 transition-transform duration-150 ease-in-out',
      )}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <span className="font-bold text-base">
          {label ?? t('Play as guest')}
        </span>
      )}
    </button>
  );
};

export { GuestLoginButton };
